<template>
	<modal
		v-model="showDialog"
		:title="$t('transfer-call')"
		:sub-title="subtitleText"
		width="600"
		:type="'question'"
		:cancel-text="$t('cancel')"
		:confirm-text="$t('transfer-call')"
		:confirm-loading="confirmLoading"
		:confirm-disabled="confirmDisabled"
		:extra-disabled="extraDisabled"
		:extra-text="extraButtonText"
		@cancel="$emit('cancel')"
		@confirm="transferCall(group)"
		@extra="onExtraButton"
	>
		<div v-if="!showSipList && enableExtTransfer">
			<v-list class="py-0 user-container mb-8 px-4">
				<v-row>
					<v-col v-for="(extensionUser) in extensions" :key="extensionUser?.id" cols="6">
						<v-card
							elevation="2"
							class="rounded"
						>
							<v-list-item
								:value="extensionUser"
								:class="{ 'selected': userExtension === extensionUser }"
								@click="userExtension = extensionUser"
							>
								<v-list-item-avatar color="initials">
									<v-icon class="white--text">
										{{ extensionUser.type === 'partner' ? 'mdi-domain' : 'mdi-phone-classic' }}
									</v-icon>
								</v-list-item-avatar>

								<v-list-item-content>
									<v-list-item-title class="font-weight-bold">
										{{ `${extensionUser.name}` }}
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-card>
					</v-col>
				</v-row>
			</v-list>
		</div>

		<div v-if="!showSipList && groups?.length">
			<v-list rounded class="py-0 user-container mb-8">
				<v-list-item-group v-model="group" color="primary">
					<v-list-item v-for="userGroup of groups" :key="userGroup.id" :value="userGroup">
						<v-list-item-avatar color="initials">
							<span class="white--text">
								{{ userGroup.label[0] }}
							</span>
						</v-list-item-avatar>

						<v-list-item-content>
							<v-list-item-title>
								{{ `${userGroup.label} ${groupPhoneNumber(userGroup.phone)}` }}
							</v-list-item-title>
						</v-list-item-content>
					</v-list-item>
				</v-list-item-group>
			</v-list>
		</div>

		<div v-if="showSipList && mergedLinesList?.length">
			<v-list class="py-0 user-container mb-8 px-4">
				<v-row>
					<v-col v-for="item in mergedLinesList" :key="item.displayName + item.sipUrl" cols="6">
						<v-card elevation="2" class="rounded">
							<v-list-item
								:value="item"
								:class="{ 'selected': userSip === item }"
								@click="userSip = item"
							>
								<v-list-item-avatar color="initials">
									<v-icon class="white--text">
										<!-- Icon depending on type -->
										{{ item.type === 'user-only' ? 'mdi-account' : 'mdi-phone-voip' }}
									</v-icon>
								</v-list-item-avatar>

								<v-list-item-content>
									<v-list-item-title class="font-weight-bold">
										{{ item.displayName }}
									</v-list-item-title>

									<!-- Only show subtitle for users with SIP -->
									<v-list-item-subtitle v-if="item.type === 'user-sip'">
										{{ item.sipName }}
									</v-list-item-subtitle>
								</v-list-item-content>
							</v-list-item>
						</v-card>
					</v-col>
				</v-row>
			</v-list>
		</div>
	</modal>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/common/Modal";
import { RequiredValidator } from "@/validators";
import { phoneNumberFormater } from "../../common/utils";

export default {
	name: "TransferCall",

	components: {
		Modal
	},

	props: {
		extensions: {
			type: Array,
			default: ()=> []
		},

		groups: {
			type: Array,
			default: () => []
		},

		open: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			group: null,
			userSip: null,
			userExtension: null,
			loading: false,
			showSipList: false,
			rules: {
				required: RequiredValidator
			},
			selectedUserGroupIndex: null,
			showDialog: this.open
		};
	},

	computed: {
		...mapGetters("clinic", ["enableExtTransfer"]),

		confirmDisabled() {
			return !this.group && !this.userExtension;
		},

		extraDisabled() {
			return !(this.mergedLinesList?.length);
		},

		extraButtonText() {
			return this.showSipList ? this.$t("back-to-groups") : this.$t("select-user");
		},

		confirmLoading() {
			return this.loading;
		},

		groupPhoneNumber() {
			return (pn) => phoneNumberFormater(pn);
		},

		mergedLinesList() {
			if (!this.group) return [];

			const sipDisplayNames = this.collectSipDisplayNames();
			const seenSipUrls = new Set();
			const mergedList = [];

			this.addUsersWithOrWithoutSip(mergedList, seenSipUrls, sipDisplayNames);
			this.addUnlinkedSips(mergedList, seenSipUrls);

			return mergedList;
		},

		subtitleText() {
			if (this.enableExtTransfer) {
				return this.$t("transfer-to-extension-info");
			}
			return this.showSipList ? this.$t("transfer-group-user-info") : this.$t("transfer-info");
		}
	},

	watch: {
		showDialog: {
			deep: true,

			handler() {
				this.loading = false;
			}
		}
	},

	methods: {
		transferCall(group) {
			this.loading = true;
			this.$emit("confirm", { group: group, userClient:this.userSip?.client?.phone, userSip: this.userSip?.sipUrl, extensionId: this.userExtension?.id });
		},

		onExtraButton() {
			if (this.userSip) {
				this.userSip = null;
			}

			this.showSipList = !this.showSipList;
		},

		collectSipDisplayNames() {
			const sipDisplayNames = {};

			if (Array.isArray(this.group?.sips)) {
				this.group.sips.forEach(sip => {
					if (sip?.url) {
						sipDisplayNames[sip.url] = sip.displayName;
					}
				});
			}
			return sipDisplayNames;
		},

		addUsersWithOrWithoutSip(mergedList, seenSipUrls, sipDisplayNames) {
			if (!Array.isArray(this.group?.users)) return;

			this.group.users.forEach(user => {
				if (!user?.displayName?.trim()) return;

				const sipUrl = user?.sip?.url;

				if (sipUrl) {
					seenSipUrls.add(sipUrl);
					mergedList.push({
						type: "user-sip",
						displayName: user.name,
						sipName: sipDisplayNames[sipUrl],
						sipUrl,
						user,
						sip: user.sip
					});
				} else {
					mergedList.push({
						type: "user-only",
						displayName: user.name,
						user
					});
				}
			});
		},

		addUnlinkedSips(mergedList, seenSipUrls) {
			if (!Array.isArray(this.group?.sips)) return;

			this.group.sips.forEach(sip => {
				const sipUrl = sip?.url;

				if (!sipUrl || seenSipUrls.has(sipUrl)) return;

				mergedList.push({
					type: "sip-only",
					displayName: sip.displayName,
					sipUrl
				});
			});
		}
	}
};
</script>

<style scoped>
.selected {
	border: 2px solid var(--v-primary-base);
	background-color: var(--v-light-primary-base);
}
</style>
